import React, { useState, useEffect } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';

const CarCard = ({ car, index, onCarClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const images = car.images || [car.image];

  useEffect(() => {
    if (!isHovered || images.length <= 1) return;
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
        setIsAnimating(false);
      }, 300);
    }, 2500);
    return () => clearInterval(interval);
  }, [isHovered, images.length]);

  const nextImage = (e) => {
    e.stopPropagation();
    if (isAnimating || images.length <= 1) return;
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
      setIsAnimating(false);
    }, 300);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    if (isAnimating || images.length <= 1) return;
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
      setIsAnimating(false);
    }, 300);
  };

  const handleCardClick = () => {
    if (onCarClick) {
      onCarClick(car);
    }
  };

  return (
    <div
      data-testid={`car-card-${car.id}`}
      className="group relative bg-black border border-amber-500/20 rounded-2xl overflow-hidden animate-fadeInUp cursor-pointer transform transition-all duration-500 hover:scale-[1.02] hover:shadow-[0_0_40px_rgba(212,160,23,0.4)] hover:border-amber-500/50"
      style={{ animationDelay: `${index * 80}ms` }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setCurrentImageIndex(0);
      }}
      onClick={handleCardClick}
    >
      {/* Featured Badge */}
      {car.featured && (
        <div className="absolute top-4 left-4 z-10 px-3 py-1 bg-gradient-to-r from-amber-500 to-amber-600 text-black text-xs font-bold rounded-full shadow-[0_0_15px_rgba(212,160,23,0.5)]">
          FEATURED
        </div>
      )}

      {/* Sale Badge */}
      {car.originalPrice && (
        <div className="absolute top-4 right-4 z-10 px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full">
          SALE
        </div>
      )}

      {/* Car Image - Full showroom image */}
      <div className="relative h-56 overflow-hidden bg-black">
        <img
          src={images[currentImageIndex]}
          alt={car.name}
          className={`w-full h-full object-cover transition-all duration-500 ${
            isAnimating ? 'opacity-0 scale-95' : 'opacity-100'
          } ${isHovered ? 'scale-105' : 'scale-100'}`}
        />

        {/* Image Navigation */}
        {images.length > 1 && isHovered && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/70 border border-amber-500/50 text-amber-500 hover:bg-amber-500 hover:text-black transition-all duration-300"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/70 border border-amber-500/50 text-amber-500 hover:bg-amber-500 hover:text-black transition-all duration-300"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </>
        )}

        {/* Image Indicators */}
        {images.length > 1 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
            {images.map((_, idx) => (
              <div
                key={idx}
                className={`h-1 rounded-full transition-all duration-300 ${
                  idx === currentImageIndex ? 'bg-amber-500 w-4' : 'bg-amber-500/30 w-1'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Car Info */}
      <div className="p-5 bg-black border-t border-amber-500/20">
        <span className="text-xs text-amber-500 font-medium uppercase tracking-wider">
          {car.category}
        </span>
        <h3 className="text-lg font-bold text-white mt-1 mb-3 group-hover:text-amber-400 transition-colors duration-300">
          {car.name}
        </h3>
        
        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-amber-400">${car.price}</span>
            <span className="text-sm text-amber-200/50">CAD/day</span>
          </div>
          {car.originalPrice && (
            <span className="text-sm text-gray-600 line-through">${car.originalPrice}</span>
          )}
        </div>

        <button
          className={`w-full mt-4 flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-all duration-500 ${
            isHovered
              ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-black'
              : 'bg-black text-amber-500 border border-amber-500/40'
          }`}
        >
          <span>View Details</span>
          <ArrowRight className={`w-4 h-4 transition-transform duration-300 ${isHovered ? 'translate-x-1' : ''}`} />
        </button>
      </div>
    </div>
  );
};

export default CarCard;
